import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const STORAGE_KEY = 'tr_cookie_consent';
const VERSION = '2026-05-13'; // bump to re-prompt all users if policy changes

const getStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.version === VERSION ? parsed : null;
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[CookieBanner] failed to read consent from localStorage', e);
    }
    return null;
  }
};

const setStored = (choice) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ choice, version: VERSION, decidedAt: new Date().toISOString() })
    );
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[CookieBanner] failed to persist consent to localStorage', e);
    }
  }
};

export const CookieBanner = () => {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Never show on admin or cookies/legal pages — user is already deep in flow
    if (pathname.startsWith('/admin')) return;
    const existing = getStored();
    if (existing) return;
    // Small delay so banner doesn't crash the hero animation
    const t = setTimeout(() => {
      setVisible(true);
      setMounted(true);
    }, 800);
    return () => clearTimeout(t);
  }, [pathname]);

  const dismiss = (choice) => {
    setStored(choice);
    if (choice === 'accepted' && typeof window.loadGoogleAnalytics === 'function') {
      window.loadGoogleAnalytics();
    }
    setVisible(false);
    setTimeout(() => setMounted(false), 380);
  };

  if (!mounted) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 50,
        backgroundColor: 'rgba(8,8,8,0.94)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        borderTop: '1px solid rgba(194,168,120,0.25)',
        padding: '22px 28px',
        transform: visible ? 'translateY(0)' : 'translateY(105%)',
        transition: 'transform 380ms cubic-bezier(0.22,1,0.36,1)',
        boxShadow: '0 -10px 40px rgba(0,0,0,0.5)',
      }}
    >
      <div
        style={{
          maxWidth: '1240px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '24px',
        }}
        className="cookie-banner-inner"
      >
        <div style={{ flex: '1 1 460px', minWidth: '260px' }}>
          <p
            id="cookie-banner-title"
            style={{
              fontSize: '10px',
              color: 'rgba(194,168,120,0.65)',
              letterSpacing: '3px',
              textTransform: 'uppercase',
              fontWeight: 400,
              marginBottom: '8px',
            }}
          >
            Your Privacy
          </p>
          <p style={{ fontSize: '13px', color: 'rgba(220,220,220,0.78)', lineHeight: 1.65, fontWeight: 300 }}>
            We use a single analytics cookie (Google Analytics) to understand how our site is used and improve the experience.
            You can accept or decline. Essential cookies for navigation are always on.{' '}
            <Link
              to="/cookies"
              data-testid="cookie-banner-learn-more"
              style={{ color: '#C2A878', textDecoration: 'underline', textUnderlineOffset: '2px' }}
            >
              Learn more
            </Link>
            .
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            data-testid="cookie-banner-reject"
            onClick={() => dismiss('rejected')}
            style={{
              padding: '11px 22px',
              fontSize: '11px',
              fontWeight: 500,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              background: 'transparent',
              color: 'rgba(220,220,220,0.85)',
              border: '1px solid rgba(220,220,220,0.3)',
              cursor: 'pointer',
              transition: 'all 200ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'rgba(220,220,220,0.6)';
              e.currentTarget.style.color = '#FAFAFA';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'rgba(220,220,220,0.3)';
              e.currentTarget.style.color = 'rgba(220,220,220,0.85)';
            }}
          >
            Decline
          </button>
          <button
            data-testid="cookie-banner-accept"
            onClick={() => dismiss('accepted')}
            style={{
              padding: '11px 26px',
              fontSize: '11px',
              fontWeight: 600,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              background: '#C2A878',
              color: '#0A0A0A',
              border: '1px solid #C2A878',
              cursor: 'pointer',
              transition: 'all 200ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#D4B888';
              e.currentTarget.style.borderColor = '#D4B888';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#C2A878';
              e.currentTarget.style.borderColor = '#C2A878';
            }}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
};

// Exposed so the Cookies policy page can offer a "change my choice" button.
export const resetCookieConsent = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.warn('[CookieBanner] failed to clear consent from localStorage', e);
    }
  }
  window.location.reload();
};
